import cx from 'classnames';
import type { FC, MouseEventHandler, ReactNode } from 'react';

import type { PaymentMethodType } from 'api/payments';
import Icon from 'components/Icon/Icon';
import Link from 'components/Link';
import { getMediaUrl } from 'utils/assets';
import useLayout from 'utils/layout';
import { Translation } from 'utils/translations';
import useIntl from 'utils/useIntl';

import styles from './PaymentItem.module.scss';

type PaymentItemProps = {
  id: number;
  name: string;
  image?: string;
  minAmount: number;
  maxAmount: number;
  method: PaymentMethodType;
  icon?: ReactNode;
  className?: string;
  imageClassName?: string;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  renderInfo?: boolean;
};

const PaymentItem: FC<PaymentItemProps> = ({
  id,
  image,
  maxAmount,
  minAmount,
  name,
  method,
  icon,
  className,
  imageClassName,
  onClick,
  renderInfo = true,
}) => {
  const imageLink = image && getMediaUrl(image);
  const layout = useLayout();
  const { formatMoney } = useIntl();

  return (
    <Link
      to={`/payment/${method.toLowerCase()}/${id}`}
      className={(active) =>
        cx(className, styles.item, styles[layout], {
          [styles.active]: active,
        })
      }
      onClick={onClick}
    >
      <div className={cx(imageClassName, styles.image)}>
        {imageLink ? (
          <img src={imageLink} alt={name} />
        ) : (
          <Icon
            width={32}
            height={32}
            className={styles.icon}
            name="payment-methods"
          />
        )}
      </div>
      {renderInfo && (
        <div className={styles.info}>
          <span className={styles.name}>{name}</span>
          <div className={styles.amount}>
            <Translation message="min_amount" />
            <span className={styles['amount-value']}>
              {formatMoney(minAmount)}
            </span>
          </div>
          <div className={styles.amount}>
            <Translation message="max_amount" />
            <span className={styles['amount-value']}>
              {formatMoney(maxAmount)}
            </span>
          </div>
        </div>
      )}

      {icon}
    </Link>
  );
};

export default PaymentItem;
